/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable, Injector } from '@angular/core';
import {
  chain,
  concat,
  entries,
  filter,
  find,
  first,
  flow,
  forEach,
  get,
  has,
  head,
  includes,
  indexOf,
  isEmpty,
  isEqual,
  isNil,
  map,
  merge,
  omit,
  partition,
  reduce,
  set,
  size,
  some,
  sortBy,
  toLower,
} from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  observableCombineLatest,
  observableOf,
  rx,
  Subscription,
} from '@shared/rx';

import { ValidationService } from '@components/form/services/validation/validation.service';
import { SitesService } from '../sites';
import {
  CreateFormRules,
  ENTITY_TYPES,
  EntityId,
  IApiListResponse,
  IBaseFormService,
  IEvent,
  IFilterQuery,
  IInventoryCountSelectedModalSites,
  ILimit,
  IModalDialogOptions,
  IQohInventoryItemVenue,
  ISite,
  ISiteForFromGridItem,
  ITransferDepartmentTotal,
  ITransferDepartmentTotalMap,
  ITransferDestination,
  ITransferFormFieldPath,
  ITransferGrandTotal,
  REPLENISH_TYPE_NAMES,
  TRANSFER_TYPE,
  XUX_GRID_ACTIONS,
  XuxGridAction,
  ToolbarButtonDefinition,
} from '@shared/types';
import { createFormRules as createTransferFormRules } from './form-rules';
import { lazyInject } from '@containers/base/decorators';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  flatTreeDataByHasChildren,
  getObservableValue,
  getSiteSelectorColumnsDefinition,
  getSubtractDateString,
  toDate,
  toMoment,
} from '@shared/utils';
import {
  TRANSFER_STATUS_NAME,
  TRANSFER_STATUS_VALUE,
} from '@shared/types/enums/transfer-status.enum';
import {
  getFormControl,
  getFormFieldValue,
  setFormFieldValue,
} from '@components/form/services/schema-form/schema-form-functions';
import {
  MAX_PAGE_SIZE,
  ONLY_DATE_SUBSTRING_LENGTH,
} from '@config/global-constants';
import { UiActions } from '@app/store/actions';
import {
  CompanyUtilService,
  DataSource,
  DmDataService,
  NumberFormatService,
  TransferService,
} from '@shared/services';
import {
  BTN_CANCEL,
  BTN_OK,
  BUTTON_ACTIONS,
} from '@text-content/button-templates';
import {
  defaultWidgetData,
  gridSelectModalOptions,
} from '@components/xux/xux-edit-grid/xux-edit-grid-widget.settings';
import { TRANSFER_MODULE } from '@app/routes';
import { getEventRelatedAndWarehouseSites } from '../transfer/transfer.util';
import { decimalNumber } from '@app/shared/common';
import {
  DEPARTMENT_I18N_CODE_MAP,
  DEPARTMENT_SORT_LIST,
  TRANSFER_UNSAVED_STATUS,
} from '@shared/services/transfer/transfer.definition';
import { InventoryItemVenueService } from '@shared/services/inventory-item-venue';
import { TransferDestinationsFormService } from '@shared/services/transfer-destinations/transfer-destinations.form.service';
import { RecipeVenueService } from '@shared/services/recipe-venue';
import { AUTO_RESTOCK } from '../transfer/transfer-constants';
import {
  composeWithDefaultEventQuery,
  getPreEventTransactionDateRange,
  getTransferEventQueryFilter,
} from '@shared/services/transfer/transfer.query.util';
import { TransferAutoRestockService } from '@shared/services/transfer-auto-restock/transfer-auto-restock.service';
import { StandWorksheetCalcServiceProvider } from '../stand-worksheet/calculation-services/stand-worksheet-calc-service-provider';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { TransferBaseFormService } from '../transfer-common';
import { ITreeData } from '../../types/common/tree.type';
import { IReplenishDestination } from '../replenish-worker/replenish.types';
import { DEFAULT_REPLENISH_PERCENT } from '../replenish-worker/replenish.constants';
import { BULK_REPLENISH_CONFIG_MAPPING } from '../transfer/transfer-replenish.constants';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
export class TransferAutoRestockFormService
  extends TransferBaseFormService
  implements IBaseFormService<ENTITY_TYPES.TRANSFER>
{
  public static ENTITY_TYPE: ENTITY_TYPES = ENTITY_TYPES.TRANSFER;

  @lazyInject(ValidationService) public validationService: ValidationService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(CompanyUtilService) public companyService: CompanyUtilService;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(TransferService) public transferService: TransferService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(TransferDestinationsFormService)
  public transferDestinationsFormService: TransferDestinationsFormService;
  @lazyInject(TransferAutoRestockService)
  public transferAutoRestockService: TransferAutoRestockService;
  @lazyInject(RecipeVenueService) public recipeVenueService: RecipeVenueService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(StandWorksheetCalcServiceProvider)
  public standWorksheetCalcServiceProvider: StandWorksheetCalcServiceProvider;

  public form: FormGroup;
  protected subscriptions: Subscription[] = [];
  public createFormRulesFunction: CreateFormRules<TransferAutoRestockFormService> =
    createTransferFormRules;
  public formFieldPath: ITransferFormFieldPath = {
    type: 'type',
    transferType: 'transfer_type',
    eventId: 'event_id',
    siteIdTo: 'site_id_to',
    siteId: 'site_id',
    siteName: 'site_name',
    siteNameTo: 'site_name_to',
    storeNumber: 'store_number',
    storeNumberTo: 'store_number_to',
    transactionDate: 'transaction_date',
    inventoryItemVenues: 'inventory_items',
    glAccountName: 'gl_account.name',
    measureEntityId: 'measure_entity_id',
    measureFactorValue: 'measure_factor_value',
    initialCount: 'initial_count',
    count: 'count',
    itemCost: 'item_cost',
    totalCost: 'total_cost',
    status: 'status',
    virtualInventoryItemVenues: 'virtual.inventory_items',
    virtualTransferDestinations: 'virtual.transfer_destinations',
    virtualGrandTotals: 'virtual.grand_totals',
    virtualTransactionDate: 'virtual.transaction_date',
    virtualSelectedEvent: 'virtual.selected_event',
  };

  public transferDateDisable: boolean = false;
  public selectedEvent: IEvent;
  public eventIdDataSource: DataSource;
  public isResetSites: boolean = true;

  public eventList$: BehaviorSubject<IEvent[]> = new BehaviorSubject([]);
  public transferDestinationsDescription$: BehaviorSubject<string> =
    new BehaviorSubject<string>(
      this.i18nService.instant(
        I18n.Forms.GridMessages.ShowingEntitiesWithSelected,
        {
          count: 0,
          entities: this.i18nService.instant(I18n.EntityNames.Sites),
          selectedCount: 0,
        },
      ),
    );
  public transferSiteList$: BehaviorSubject<ISite[]> = new BehaviorSubject([]);
  public sitesDisplayCount$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  public siteModalDescription$: BehaviorSubject<string> =
    new BehaviorSubject<string>(
      this.i18nService.instant(I18n.Forms.GridMessages.ShowingEntities, {
        count: 0,
        entities: this.i18nService.instant(
          I18n.EntityNames.siteSingularAndPlural,
        ),
      }),
    );
  public disableAddSite$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public disableBulkReplenishToPAR$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);
  public isTransferDestinationsNotEmpty$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  public transferDestinationsDisplayCount$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  public transferDestinationsSelectedCount$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  public transactionDateLimit: ILimit<string | null> = {
    minimumObs: new BehaviorSubject(
      getSubtractDateString(toDate().toISOString(), ONLY_DATE_SUBSTRING_LENGTH),
    ),
    maximumObs: new BehaviorSubject(null),
  };
  public hiddenDestinationInfo$: BehaviorSubject<boolean> = new BehaviorSubject(
    true,
  );
  public hiddenDestinationNoSitesInfo$: BehaviorSubject<boolean> =
    new BehaviorSubject(true);
  public isGridBusy$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private destinationGrid: XuxEditGridComponent;

  private pageInitialTime: string;

  public constructor(public injector: Injector) {
    super(injector);
    this.pageInitialTime = toDate().toISOString();
    this.initEventDataSource();
  }

  public async onFormReady(form: FormGroup): Promise<void> {
    this.form = form;
    // need await this to make sure event is requested
    await this.initDefaultEvent();
    // this need call after initDefaultEvent is done
    this.initDefaultTransferDate();
    this.initTransferDestinationsData();
    this.initEventSubscription();
    this.initEventIdSubscription();
    this.initTransferDestinationsSubscription();
    this.initDescriptionSubscriptions();

    this.eventIdDataSource?.refresh();
  }

  public initDescriptionSubscriptions(): void {
    this.subscriptions.push(
      observableCombineLatest([
        this.transferDestinationsDisplayCount$,
        this.transferDestinationsSelectedCount$,
      ])
        .pipe(rx.distinctUntilChanged(isEqual))
        .subscribe(
          ([displayedCount, selectedCount]: [number, number]): void => {
            const sitesSubTitle = this.i18nService.instant(
              I18n.Forms.GridMessages.ShowingEntitiesWithSelected,
              {
                count: displayedCount,
                entities: this.i18nService.instant(I18n.EntityNames.Sites),
                selectedCount,
              },
            );
            this.transferDestinationsDescription$.next(sitesSubTitle);
          },
        ),
    );
    this.subscriptions.push(
      this.sitesDisplayCount$.subscribe((count: number): void => {
        const sitesSubTitle = this.i18nService.instant(
          I18n.Forms.GridMessages.ShowingEntities,
          {
            count,
            entities: this.i18nService.instant(I18n.EntityNames.Sites),
          },
        );
        this.siteModalDescription$.next(sitesSubTitle);
      }),
    );
  }

  public destroyFormDefinition(): void {
    this.resetTransferEffectiveDates();
    this.isGridBusy$.next(true);
    this.hiddenDestinationInfo$.next(true);
    this.hiddenDestinationNoSitesInfo$.next(true);
    this.isResetSites = true;
    super.destroyFormDefinition();
  }

  public getTransferDateDisable(): boolean {
    return this.transferDateDisable;
  }

  public initTransferDestinationsSubscription(): void {
    const subscription = this.fieldValueChanges(
      this.formFieldPath.virtualTransferDestinations,
    ).subscribe((value) => {
      this.updateGrandTotalsData(value);
      this.isTransferDestinationsNotEmpty$.next(!isEmpty(value));

      const hasOneWarehouse = find(
        value,
        (site) => !this.noPrimaryWarehouse(site),
      );

      const matchSite = find(value, (site) => this.noPrimaryWarehouse(site));

      this.disableBulkReplenishToPAR$.next(isEmpty(value) || !hasOneWarehouse);
      this.disableAddSite$.next(isEmpty(value));
      this.hiddenDestinationInfo$.next(!matchSite);
      this.hiddenDestinationNoSitesInfo$.next(!isEmpty(value));
    });
    this.subscriptions.push(subscription);
  }

  public initTransferDestinationsData(): void {
    const subs = this.transferSiteList$.subscribe((data) => {
      if (this.isResetSites) {
        // Avoid the value of virtualTransferDestinations influences the data of transferSiteList$
        setFormFieldValue(
          this.form,
          this.formFieldPath.virtualTransferDestinations,
          map(data, (site) => ({
            ...site,
            status: TRANSFER_STATUS_VALUE.Null,
          })),
          false,
        );
      }
    });
    this.subscriptions.push(subs);
  }

  public initEventSubscription(): void {
    const subscription = this.fieldValueChanges(this.formFieldPath.eventId)
      .pipe(
        rx.filter(() => {
          const isTouched = getFormControl(
            this.form,
            this.formFieldPath.eventId,
          )?.touched;

          if (
            !isEmpty(
              getFormFieldValue(this.form, this.formFieldPath.eventId),
            ) &&
            !isTouched
          ) {
            this.isGridBusy$.next(false);
          }

          return isTouched;
        }),
        rx.distinctUntilChanged(),
      )
      .subscribe(this.onEventChange.bind(this));
    this.subscriptions.push(subscription);
  }

  public initDefaultTransferDate(): void {
    const dateValue = getFormFieldValue(
      this.form,
      this.formFieldPath.transactionDate,
    );

    const autoPopulateDate = this.getAutoPopulateForNewData<string>({
      targetFieldPath: this.formFieldPath.transactionDate,
    });

    let transactionDate = null;

    if (!isEmpty(autoPopulateDate)) {
      const minDate = getObservableValue(this.transactionDateLimit.minimumObs);
      const maxDate = getObservableValue(this.transactionDateLimit.maximumObs);

      if (
        toMoment(autoPopulateDate).isSameOrAfter(toMoment(minDate)) &&
        toMoment(autoPopulateDate).isSameOrBefore(toMoment(maxDate))
      ) {
        transactionDate = autoPopulateDate;
      }
    } else if (isNil(dateValue) || isEmpty(dateValue)) {
      transactionDate = this.companyService.getCurrentTime();
    }

    if (!isNil(transactionDate)) {
      setFormFieldValue(
        this.form,
        this.formFieldPath.transactionDate,
        transactionDate,
        false,
      );
    }
  }

  public async initDefaultEvent(): Promise<void> {
    const eventValue = getFormFieldValue(this.form, this.formFieldPath.eventId);
    const autoPopulateEventId = this.getAutoPopulateForNewData({
      targetFieldPath: this.formFieldPath.eventId,
    });

    const isHaveAutoPopulateEventId = !isEmpty(autoPopulateEventId);

    if (isEmpty(eventValue) || isHaveAutoPopulateEventId) {
      this.transferSiteList$.next([]);
      const eventList = (await this.eventIdDataSource.nextPage()) as IEvent[];

      this.selectedEvent = isHaveAutoPopulateEventId
        ? (find(eventList, { id: autoPopulateEventId }) as IEvent)
        : this.getMostRecentEventAfterNow(eventList);

      setFormFieldValue(
        this.form,
        this.formFieldPath.eventId,
        this.selectedEvent?.id,
        false,
      );
      this.getTransferSiteList();
    }

    if (isNil(this.selectedEvent)) {
      const eventList = getObservableValue(this.eventList$);

      this.selectedEvent = find(eventList, { id: eventValue });
    }

    const salesStartDateTimeUTC = head(
      this.selectedEvent?.event_days,
    )?.start_date_time;
    const companyCurrentDate = this.companyService.getCurrentTime();
    const salesStartDate = this.companyService.convertUTCDateWithTimeZone(
      salesStartDateTimeUTC,
      true,
    );
    const { minimum, maximum } = getPreEventTransactionDateRange(
      companyCurrentDate,
      salesStartDate,
    );

    this.transactionDateLimit.minimumObs.next(minimum);
    this.transactionDateLimit.maximumObs.next(maximum);
  }

  public getMostRecentEventAfterNow(eventList: IEvent[]): IEvent | undefined {
    const currentTime = toMoment().utc().milliseconds(0);
    let eventsListAfterNow: IEvent;
    some(eventList, (event: IEvent) => {
      const eventStartTime = get(
        head(get(event, 'event_days')),
        'start_date_time',
      );
      if (toMoment(eventStartTime).isAfter(toMoment(currentTime))) {
        return (eventsListAfterNow = event);
      }
    });

    return eventsListAfterNow;
  }

  public initEventDataSource(): void {
    this.eventIdDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.EVENTS,
    );
    this.eventIdDataSource.fetchList = this.eventDataSourceFetchList.bind(this);
  }

  public fetchSiteList(): Promise<any> {
    this.isGridBusy$.next(true);
    this.hiddenDestinationInfo$.next(true);
    this.hiddenDestinationNoSitesInfo$.next(true);
    if (!this.form) {
      this.isGridBusy$.next(false);

      return Promise.resolve({
        items: [],
      } as IApiListResponse<any>);
    }

    const eventIdValue = this.getFormFieldValue(this.formFieldPath.eventId);

    const queryWithDefaults: IFilterQuery = {
      filter: `is_active eq true`,
      select: [
        'name',
        'id',
        'type',
        'business_type',
        'store_number',
        'metadata',
      ],
      filter_by_assigned_sites: true,
      include_inactive: false,
      top: MAX_PAGE_SIZE,
    };

    if (this.isEventType()) {
      if (isNil(eventIdValue)) {
        this.isGridBusy$.next(false);

        return Promise.resolve({
          items: [],
        } as IApiListResponse<any>);
      }
      queryWithDefaults.top = MAX_PAGE_SIZE;
    }

    return this.sitesService
      .fetchList(queryWithDefaults)
      .pipe(
        rx.first(),
        rx.switchMap((allSites) => {
          const eventSites: ISite[] = get(
            this.selectedEvent,
            'available_sites',
            [],
          );
          const sites: ISite[] = allSites.items as ISite[];
          const sitesList = getEventRelatedAndWarehouseSites(sites, eventSites);
          const siteIds: string[] = map(sitesList, 'id');

          return observableCombineLatest([
            observableOf(sitesList),
            this.dmDataService.getSiteGroupId(siteIds),
          ]);
        }),
        rx.switchMap(([sites, siteGroupId]) => {
          const sitePreferences =
            this.dmDataService.getSitesPreferencesBySiteGroupId(siteGroupId);

          return observableCombineLatest([
            observableOf(sites),
            sitePreferences,
          ]);
        }),
        rx.map(([sites, preferences]) => {
          const response = {
            total: 0,
            items: [],
          };

          if (!this.form) return { ...response, items: [] };

          const setWarehouseForSites = this.setPrimaryWarehousetoSites(
            sites,
            preferences,
          );
          const sitesList =
            this.setSitesPrimaryWarehouseTop(setWarehouseForSites);
          response.total = size(sitesList);
          response.items = sitesList;

          this.isGridBusy$.next(false);

          return response;
        }),
        rx.finalize(() => {
          this.isGridBusy$.next(false);
        }),
      )
      .toPromise();
  }

  public setPrimaryWarehousetoSites(
    sites,
    sitePreferences,
  ): ISiteForFromGridItem[] {
    return map(sites, (site) => {
      const preference = find(sitePreferences, (sitePreference) =>
        isEqual(sitePreference.site_id, site.id),
      );
      set(site, 'disabled', true);

      const primaryWarehouseId = get(
        preference,
        ['preferences', 'back_office', 'warehouse', 'primary_warehouse'],
        '',
      );

      if (!isEmpty(primaryWarehouseId)) {
        const primaryWarehouse = find(sites, (targetSite) =>
          isEqual(primaryWarehouseId, targetSite.id),
        );
        const warehouseName = get(primaryWarehouse, 'name', '');
        set(site, 'site_id', primaryWarehouseId);
        set(site, 'site_name', warehouseName);
        set(site, 'disabled', isEmpty(warehouseName));
        set(site, 'replenish_percent', '100');
      } else {
        set(site, 'site_id', '');
        set(site, 'site_name', '');
      }

      return site;
    });
  }

  public setSitesPrimaryWarehouseTop(sites: ISiteForFromGridItem[]): ISite[] {
    const sortedList = sortBy(sites, (item) => toLower(item.name));

    const [hasWarehouseSites, noWarehouseSites] = partition(
      sortedList,
      (item) => !this.noPrimaryWarehouse(item),
    );

    return concat(hasWarehouseSites, noWarehouseSites);
  }

  public eventDataSourceFetchList(
    query: IFilterQuery,
  ): Promise<IApiListResponse> {
    if (!this.form || !this.isEventType()) {
      return Promise.resolve({
        items: [],
      } as IApiListResponse<any>);
    }

    const userQuery = {
      filter: getTransferEventQueryFilter(TRANSFER_TYPE.PRE_EVENT_TRANSFER),
      orderby: 'event_days/start_date_time asc',
    };
    const eventQuery = composeWithDefaultEventQuery(userQuery);

    return this.eventIdDataSource.service
      .fetchList(eventQuery)
      .pipe(
        rx.first(),
        rx.map((response) => {
          this.eventList$.next(response.items as IEvent[]);

          return response;
        }),
      )
      .toPromise()
      .then(
        (result) => result,
        (error) => (
          this.eventIdDataSource.error.next(error), Promise.reject(error)
        ),
      );
  }

  public initEventIdSubscription(): void {
    const subscription = observableCombineLatest([
      this.eventList$.pipe(rx.filter((events) => !isEmpty(events))),
      this.fieldValueChanges(this.formFieldPath.eventId),
    ]).subscribe(([events, eventId]: [IEvent[], string]) => {
      const selectedEvent = find(events, { id: eventId });
      this.selectedEvent = selectedEvent;

      setFormFieldValue(
        this.form,
        this.formFieldPath.virtualSelectedEvent,
        selectedEvent,
        false,
      );
    });
    this.subscriptions.push(subscription);
  }

  public onEventChange(eventId): void {
    this.isResetSites = true;
    this.transferSiteList$.next([]);

    const selectedEvent = find(getObservableValue(this.eventList$), {
      id: eventId,
    });
    const salesStartDateTimeUTC = head(
      selectedEvent?.event_days,
    )?.start_date_time;
    const companyCurrentDate = this.companyService.getCurrentTime();
    const salesStartDate = this.companyService.convertUTCDateWithTimeZone(
      salesStartDateTimeUTC,
      true,
    );
    const { minimum, maximum } = getPreEventTransactionDateRange(
      companyCurrentDate,
      salesStartDate,
    );

    this.transactionDateLimit.minimumObs.next(minimum);
    this.transactionDateLimit.maximumObs.next(maximum);

    this.getTransferSiteList();

    setFormFieldValue(
      this.form,
      this.formFieldPath.transactionDate,
      companyCurrentDate,
    );
    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransactionDate,
      salesStartDateTimeUTC,
    );
  }

  public isEventType(): boolean {
    const transferTypeValue: TRANSFER_TYPE = getFormFieldValue(
      this.form,
      this.formFieldPath.transferType,
    );
    const eventTypes = [
      TRANSFER_TYPE.PRE_EVENT_TRANSFER,
      TRANSFER_TYPE.DURING_EVENT_TRANSFER,
    ];

    return includes(eventTypes, transferTypeValue);
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public isTransferByType(
    condition: TRANSFER_TYPE,
    typeValue?: TRANSFER_TYPE,
  ): boolean {
    const transferTypeValue: TRANSFER_TYPE = typeValue
      ? typeValue
      : getFormFieldValue(this.form, this.formFieldPath.transferType);

    return isEqual(condition, transferTypeValue);
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public isPreEventTypeTransfer(type?: TRANSFER_TYPE): boolean {
    return this.isTransferByType(TRANSFER_TYPE.PRE_EVENT_TRANSFER, type);
  }

  public resetTransferEffectiveDates(): void {
    this.transferDateDisable = false;
    this.transactionDateLimit.minimumObs.next(
      getSubtractDateString(toDate().toISOString(), ONLY_DATE_SUBSTRING_LENGTH),
    );
    this.transactionDateLimit.maximumObs.next(null);
  }

  public async openSiteSelector(): Promise<void> {
    const virtualTransferDestinationsValue = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferDestinations,
    );
    const transferDestinationIds = map(virtualTransferDestinationsValue, 'id');
    const transferSitesList$: Observable<ISite[]> =
      this.transferSiteList$.first();
    const transferSitesListPromise: ISite[] =
      await transferSitesList$.toPromise();
    const entityIdsForMetadatas: EntityId[] = chain(transferSitesListPromise)
      .flatMap('metadata')
      .map('entity_id')
      .value();

    const siteSelectorGridSettings: IModalDialogOptions = {
      title: I18n.Common.SiteSelector,
      subTitle: this.siteModalDescription$,
      modalClass: `modal-lg`,
      buttons: [
        {
          ...BTN_OK,
          caption: this.getSiteSelectorButtonCaption.bind(this),
          class: 'pull-right btn-primary',
        },
        BTN_CANCEL,
      ],
      schema: {
        properties: {
          modified_message: { type: 'string' },
          items: { type: 'array', required: true },
        },
      },
      formRules: {
        fields: {
          items: {
            ignoreAutoTitleWhenTranslate: true,
            widgetData: {
              displayCountObs: this.sitesDisplayCount$,
              valueField: 'id',
              treeViewMode: true,
              treeChildrenPropertyName: 'children',
              treeColumnTitle: 'hierarchy',
              defaultExpanded: false,
              ignoreItemValueOptimization: true,
              useVisibleItemCountDescription: true,
              searchPlaceholder: this.i18nService.instant(I18n.Common.Search),
              columns: getSiteSelectorColumnsDefinition(entityIdsForMetadatas),
              userGridOptions: {
                overlayNoRowsTemplate: I18n.Common.NoRowsToShow,
                isRowSelectable: ({ data }): boolean =>
                  !includes(transferDestinationIds, data.id),
                getRowClass: ({ data }): string =>
                  includes(transferDestinationIds, data.id)
                    ? 'row-disabled'
                    : '',
              },
            },
          },
        },
      },
    };

    const modalOptions: IModalDialogOptions = merge(
      {},
      gridSelectModalOptions(transferSitesList$, this.i18nService),
      defaultWidgetData,
      siteSelectorGridSettings,
    );

    const { confirmed, value } =
      await this.uiActions.formModalDialog(modalOptions);
    if (!confirmed) return;

    const additionalItems = map(value.items, (item) =>
      merge({}, item, { status: TRANSFER_STATUS_VALUE.Null }),
    );

    const transferDestinationsData = this.setSitesPrimaryWarehouseTop([
      ...virtualTransferDestinationsValue,
      ...additionalItems,
    ]);

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferDestinations,
      transferDestinationsData,
    );
  }

  public getBulkReplenishClickHandler(btn: ToolbarButtonDefinition): Function {
    return async (): Promise<void> => {
      const btnReplenishConfig =
        BULK_REPLENISH_CONFIG_MAPPING[btn.caption as string];

      const virtualTransfers: ITransferDestination[] = this.getFormFieldValue(
        'virtual.transfer_destinations',
      );

      const unsavedVirtualTransfers = filter(
        virtualTransfers,
        (transfer) =>
          !transfer.disabled &&
          (isNil(transfer.status) ||
            includes(
              [...TRANSFER_UNSAVED_STATUS, TRANSFER_STATUS_NAME.New],
              transfer.status,
            )),
      );

      await this.replenishWorkerBuilder
        .build({
          additionDataOpts: {
            preInventory: [
              () => ({
                event: this.selectedEvent,
              }),
            ],
            afterInventory: btnReplenishConfig.afterInventory,
          },
          on: {
            beforeReplenish: () => {
              this.destinationGrid.xuxGridComponent.gridApi.setFilterModel(
                null,
              );
            },
            afterReplenish: (replenishRes) => {
              const { destinations } = replenishRes;

              const replenishedDestinations =
                destinations as IReplenishDestination[];

              const virtualTransferItems: any[] = this.getFormFieldValue(
                'virtual.transfer_destinations',
              );

              forEach(replenishedDestinations, (destination) => {
                const virtualTransferItem = find(virtualTransferItems, {
                  id: destination.toSite,
                });

                if (!isNil(virtualTransferItem)) {
                  merge(
                    virtualTransferItem,
                    omit(destination, [
                      'percent',
                      'fromSiteName',
                      'fromSite',
                      'toSite',
                      'toSiteName',
                      'toSiteStoreNumber',
                    ]),
                  );
                  set(
                    virtualTransferItem,
                    'inventory_items',
                    destination.inventory_items,
                  );
                }
              });

              setFormFieldValue(
                this.form,
                this.formFieldPath.virtualTransferDestinations,
                virtualTransferItems,
              );
            },
          },
        })
        .replenish({
          destination: map(unsavedVirtualTransfers, (transfer) => ({
            fromSite: transfer.site_id,
            fromSiteName: transfer['site_name'],
            toSite: transfer.id,
            toSiteName: transfer.name,
            toSiteStoreNumber: transfer.store_number,
            inventory_items: transfer.inventory_items,
            percent: transfer.replenish_percent ?? DEFAULT_REPLENISH_PERCENT,
          })),
          strategy: btnReplenishConfig.strategy,
          messages: {
            noAvailable: this.i18nService.instant(
              I18n.ErrorMessages.BulkReplenishNoAvailableMessage,
              {
                type: REPLENISH_TYPE_NAMES.PAR,
              },
            ),
            error: this.i18nService.instant(
              I18n.InfoMessages.BulkReplenishFailedMessage,
            ),
            success: this.i18nService.instant(
              I18n.InfoMessages.BulkReplenishSuccessMessage,
              {
                replenishType: this.i18nService.instant(I18n.Common.PAR),
              },
            ),
          },
          confirmModalOptions: {
            modalTitle: btnReplenishConfig.modalTitle,
          },
        });
    };
  }

  public adjustTransferDestinationStatus(
    destination: ISiteForFromGridItem,
  ): ISiteForFromGridItem {
    const allItemsForCurrentSite = flatTreeDataByHasChildren(
      get(destination, 'inventory_items', []) as ITreeData[],
    );
    if (
      some(allItemsForCurrentSite, (item) => {
        return (
          item[this.formFieldPath.initialCount] > 0 ||
          item[this.formFieldPath.initialCount] < 0
        );
      })
    ) {
      destination.status = TRANSFER_STATUS_VALUE.New;
    } else {
      destination.status = TRANSFER_STATUS_VALUE.Null;
    }

    return destination;
  }

  public mergeInventoryItems(
    allInventoryItems: IQohInventoryItemVenue[],
    virtualInventoryItems: (IQohInventoryItemVenue | ITreeData)[],
  ): IQohInventoryItemVenue[] {
    if (isEmpty(virtualInventoryItems)) {
      return allInventoryItems;
    }

    return map(allInventoryItems, (item) => {
      const findItemInVirtual = find(
        virtualInventoryItems,
        (virtualItem) =>
          isEqual(get(virtualItem, 'site_id'), get(item, 'site_id')) &&
          isEqual(get(virtualItem, 'entity_id'), get(item, 'entity_id')),
      );

      if (!isEmpty(findItemInVirtual)) {
        return merge({}, item, findItemInVirtual);
      }

      return item;
    });
  }

  public getSiteSelectorButtonCaption(
    selectedModalSites: IInventoryCountSelectedModalSites,
  ): string {
    const itemsLength = selectedModalSites.items.length;

    return itemsLength > 1
      ? this.i18nService.instant(I18n.Common.SelectXEntities, {
          count: itemsLength,
          entities: this.i18nService.instant(
            I18n.EntityNames.SiteSingularAndPlural,
          ),
        })
      : this.i18nService.instant(I18n.Common.SelectSitesWithBracket);
  }

  public async editSiteInventoryItems(
    gridItem: ISiteForFromGridItem,
  ): Promise<void> {
    const routeUri = `${TRANSFER_MODULE}/destinations`;

    this.uiActions.removeNotificationsOnPageLeave();

    this.uiActions.openRoute(routeUri, {
      state: {
        selectedSite: gridItem,
        storeForm: {
          ...this.form.value,
          site_id: gridItem.site_id,
          site_name: gridItem.site_name,
          site_id_to: gridItem.id,
          site_name_to: gridItem.name,
        },
        origin_from: AUTO_RESTOCK,
        create_time: this.pageInitialTime,
        isFormDirty: this.form.dirty,
      },
    });
  }

  public async getTransferSiteList(): Promise<void> {
    const response = await this.fetchSiteList().then((result) => result);

    this.transferSiteList$.next(response.items as ISite[]);
  }

  public composeConfirmationModalOptions(action: string): IModalDialogOptions {
    if (action === BUTTON_ACTIONS.REMOVE) {
      return {
        text: I18n.InfoMessages.ConfirmationModalRemovesSites,
      };
    }
  }

  public getTransferDestinationsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const controlRoot = control.root;

      if (isNil(controlRoot.value)) return null;
      const hasNewStatusTransfer: boolean = some(control.value, (item) =>
        includes(
          [TRANSFER_STATUS_NAME.New, TRANSFER_STATUS_VALUE.New],
          item.status,
        ),
      );
      const translatedErrorMessage = this.i18nService.instant(
        I18n.ErrorMessages.AtLeastOneTransferShouldBeInANewState,
      );

      return hasNewStatusTransfer ? null : { error: translatedErrorMessage };
    };
  }

  public handleDepartmentAndTotalCostFields(
    data: ITransferDestination[],
  ): ITransferDepartmentTotal[] {
    return map(data, (item: ITransferDestination): ITransferDepartmentTotal => {
      const departmentCode = find(item.metadata, (metaItem) =>
        isEqual(metaItem.name, 'Department Code'),
      );

      return {
        departmentCode: get(departmentCode, 'value', ''),
        totalCost: item.totalCost,
      };
    }).filter(
      (item: ITransferDepartmentTotal) => size(item.departmentCode) > 0,
    );
  }

  public statisticsRequestCostByDepartment(
    data: ITransferDepartmentTotal[],
  ): ITransferDepartmentTotalMap {
    return reduce(
      data,
      (prev, curr: ITransferDepartmentTotal) => {
        if (has(prev, curr.departmentCode)) {
          prev[curr.departmentCode] = decimalNumber(
            prev[curr.departmentCode] || 0,
          )
            .add(curr.totalCost || 0)
            ?.valueOf();
        } else {
          prev[curr.departmentCode] = decimalNumber(curr.totalCost)?.valueOf();
        }

        return prev;
      },
      {},
    );
  }

  public convertDepartmentTotalMapToArray(
    data: ITransferDepartmentTotalMap,
  ): ITransferGrandTotal[] {
    const departmentTotalDataList: ITransferGrandTotal[] = map(
      entries(data),
      ([key, value]) => {
        return {
          department_code: key,
          request_cost: value,
        };
      },
    );

    return flow(
      (items: ITransferGrandTotal[]) =>
        sortBy(items, (item) =>
          indexOf(DEPARTMENT_SORT_LIST, item.department_code),
        ),
      (sortedItems: ITransferGrandTotal[]) =>
        map(sortedItems, (item) => ({
          ...item,
          department_code: this.i18nService.instant(
            get(
              DEPARTMENT_I18N_CODE_MAP,
              item.department_code,
              item.department_code,
            ),
          ),
        })),
    )(departmentTotalDataList);
  }

  public updateGrandTotalsData(data: ITransferDestination[]): void {
    const departmentTotal: ITransferDepartmentTotal[] =
      this.handleDepartmentAndTotalCostFields(data);
    const departmentTotalMap: ITransferDepartmentTotalMap =
      this.statisticsRequestCostByDepartment(departmentTotal);
    const departmentTotalArray: ITransferGrandTotal[] =
      this.convertDepartmentTotalMapToArray(departmentTotalMap);
    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualGrandTotals,
      departmentTotalArray,
      false,
    );
  }

  public isRowSelectable(params): boolean {
    return !this.noPrimaryWarehouse(params.data);
  }

  public noPrimaryWarehouse(data): boolean {
    return data.disabled;
  }

  /**
   * handle when list action
   *
   * @param action xux grid action
   * @param grid edit grid object
   */
  public handleDestinationGridAction(
    action: XuxGridAction,
    grid: XuxEditGridComponent,
  ): void {
    if (isEqual(action.type, XUX_GRID_ACTIONS.GRID_READY)) {
      this.destinationGrid = grid;
    }
    if (isEqual(action.type, XUX_GRID_ACTIONS.ROW_DOUBLE_CLICKED)) {
      const selectedSite = first(action.payload.rowData as ISite[]);
      if (!this.noPrimaryWarehouse(selectedSite)) {
        this.editSiteInventoryItems(selectedSite);
      }
    }
  }
  // eslint-disable-next-line max-lines
}
